import { useMemo } from "react";
import {
  EntityPickerField,
  type TypedEntityPickerProps,
} from "./EntityPickerField";
import type { PickerEntity } from "./entity";
import { useAutoTransactionRules } from "../../api/queries";
import type { AutoTransactionRuleDto, AutoTxId } from "../../api/types";

export type AutoTransactionPickerFieldProps = TypedEntityPickerProps<AutoTxId>;

/**
 * Picker for auto-transaction rules. Wraps the generic `EntityPickerField`
 * with a "auto-transaction" kind so it gets the searchable dialog UX used
 * across Source / Group / etc. — searchable, grouped, scrollable —
 * which is the only way that's tolerable when there are hundreds of rules.
 *
 * Rules don't live in the entity registry, so we pull them from
 * `useAutoTransactionRules` and map to `PickerEntity`. Disabled rules are
 * surfaced (so a user debugging "why isn't this firing" can find them) but
 * shown with the disabled style and grouped under their own subheader.
 *
 * Inline create is always off — rule creation is its own form.
 */
export const AutoTransactionPickerField = (
  props: AutoTransactionPickerFieldProps,
) => {
  const { data: rules } = useAutoTransactionRules();

  const items = useMemo<PickerEntity[]>(() => {
    const list = (rules ?? []).map(toPickerEntity);
    list.sort((a, b) => {
      // Enabled first, then alphabetical (case-insensitive).
      const aDisabled = a.style === "italic" ? 1 : 0;
      const bDisabled = b.style === "italic" ? 1 : 0;
      if (aDisabled !== bDisabled) return aDisabled - bDisabled;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
    return list;
  }, [rules]);

  const groupBy = (item: PickerEntity) =>
    item.style === "italic" ? "Disabled" : "Enabled";

  return (
    <EntityPickerField<AutoTxId>
      kind="auto-transaction"
      items={items}
      groupBy={groupBy}
      allowCreate={false}
      {...props}
    />
  );
};

const toPickerEntity = (rule: AutoTransactionRuleDto): PickerEntity => ({
  id: rule.id,
  name: rule.name,
  description: rule.description ?? undefined,
  // Auto-transaction is in the no-avatar set in config.ts; `updated` is only
  // used to build the logo URL, so an empty string is fine here.
  updated: "",
  style: rule.enabled ? null : "italic",
});
